import {
    Box,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    FormControlLabel,
    Switch,
} from "@mui/material";
import {
    PlayArrow as PlayIcon,
    ExpandMore as ExpandMoreIcon,
    CloudUpload as UploadIcon,
    Storage as BlobIcon,
} from "@mui/icons-material";
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { testPrompt, clearTestResult } from "../../redux/reducer/prompts";
import { PromptDetail, TestResult, TaxReturnPage, DefaultFormInfo } from "../../interface/prompts";
import Button from "../atoms/Button";

interface PromptTesterProps {
    currentContent: string;
}

const PromptTester = ({
    currentContent,
}: PromptTesterProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const selectedPrompt = useSelector<RootState, PromptDetail | null>(({ prompts }) => prompts.selectedPrompt);
    const testResult = useSelector<RootState, TestResult | null>(({ prompts }) => prompts.testResult);
    const testLoading = useSelector<RootState, boolean>(({ prompts }) => prompts.testLoading);

    const [testType, setTestType] = useState<'parser' | 'extractor'>('parser');
    const [pdfSource, setPdfSource] = useState<'upload' | 'blob'>('upload');
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [containerName, setContainerName] = useState<string>("");
    const [fileName, setFileName] = useState<string>("");
    const [parserResultsJson, setParserResultsJson] = useState<string>("");
    const [parserResultsError, setParserResultsError] = useState<string>("");
    const [useDefaultFormInfo, setUseDefaultFormInfo] = useState<boolean>(true);

    const fileInputRef = useRef<HTMLInputElement>(null);

    // Parse form type and tax year from extractor prompt name
    const parseExtractorPromptName = (promptName: string): DefaultFormInfo | null => {
        // Pattern: extractor/{formType}-extract-only[-{taxYear}].txt
        const match = promptName.match(/^extractor\/(.+)-extract-only(?:-(\d{4}))?\.txt$/);
        if (!match) return null;

        const formType = match[1];
        const taxYear = match[2] ? parseInt(match[2], 10) : new Date().getFullYear() - 1;

        return { type: formType, taxYear };
    };

    const getDefaultFormInfo = (): DefaultFormInfo | null => {
        if (!selectedPrompt) return null;
        return parseExtractorPromptName(selectedPrompt.name);
    };

    // Default test type based on prompt folder
    useEffect(() => {
        if (selectedPrompt) {
            const isExtractor = selectedPrompt.name.startsWith('extractor/');
            setTestType(isExtractor ? 'extractor' : 'parser');
        }
    }, [selectedPrompt]);

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.type !== 'application/pdf') {
            return;
        }

        setPdfFile(file);
    };

    const validateParserResults = (): TaxReturnPage[] | null => {
        if (testType !== 'extractor') return null;
        if (!parserResultsJson.trim()) {
            setParserResultsError("Parser results are required for extractor type");
            return null;
        }
        try {
            const parsed = JSON.parse(parserResultsJson);
            if (!Array.isArray(parsed)) {
                setParserResultsError("Parser results must be an array");
                return null;
            }
            setParserResultsError("");
            return parsed as TaxReturnPage[];
        } catch {
            setParserResultsError("Invalid JSON format");
            return null;
        }
    };

    const handleRunTest = () => {
        if (!selectedPrompt) return;

        dispatch(clearTestResult());

        let parserResults: TaxReturnPage[] | undefined;
        let defaultFormInfo: DefaultFormInfo | undefined;

        if (testType === 'extractor') {
            if (useDefaultFormInfo) {
                const formInfo = getDefaultFormInfo();
                if (!formInfo) {
                    setParserResultsError("Could not parse form info from prompt name");
                    return;
                }
                defaultFormInfo = formInfo;
            } else {
                const validated = validateParserResults();
                if (!validated) return;
                parserResults = validated;
            }
        }

        if (pdfSource === 'upload') {
            if (!pdfFile) return;
            dispatch(testPrompt({
                input: {
                    type: testType,
                    text: currentContent,
                    pdfFile,
                    parserResults,
                    defaultFormInfo,
                },
            }));
        } else {
            if (!containerName || !fileName) return;
            dispatch(testPrompt({
                input: {
                    type: testType,
                    text: currentContent,
                    blobSource: { containerName, fileName },
                    parserResults,
                    defaultFormInfo,
                },
            }));
        }
    };

    const canRunTest = () => {
        if (!selectedPrompt) return false;
        if (pdfSource === 'upload' && !pdfFile) return false;
        if (pdfSource === 'blob' && (!containerName || !fileName)) return false;
        if (testType === 'extractor' && !useDefaultFormInfo && !parserResultsJson.trim()) return false;
        if (testType === 'extractor' && useDefaultFormInfo && !getDefaultFormInfo()) return false;
        return true;
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (!selectedPrompt) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    Select a prompt to test
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Test Type */}
            <FormControl fullWidth size="small">
                <InputLabel>Test Type</InputLabel>
                <Select
                    value={testType}
                    label="Test Type"
                    onChange={(e) => setTestType(e.target.value as 'parser' | 'extractor')}
                >
                    <MenuItem value="parser">Parser</MenuItem>
                    <MenuItem value="extractor">Extractor</MenuItem>
                </Select>
            </FormControl>

            {/* PDF Source Tabs */}
            <Paper variant="outlined">
                <Tabs
                    value={pdfSource}
                    onChange={(_, newValue) => setPdfSource(newValue)}
                    variant="fullWidth"
                >
                    <Tab
                        icon={<UploadIcon />}
                        iconPosition="start"
                        label="Upload PDF"
                        value="upload"
                    />
                    <Tab
                        icon={<BlobIcon />}
                        iconPosition="start"
                        label="Blob Reference"
                        value="blob"
                    />
                </Tabs>

                <Box sx={{ p: 2 }}>
                    {pdfSource === 'upload' ? (
                        <Box>
                            <input
                                type="file"
                                accept=".pdf,application/pdf"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                            />
                            <Button
                                variant="outlined"
                                onClick={() => fileInputRef.current?.click()}
                                startIcon={<UploadIcon />}
                            >
                                {pdfFile ? 'Change PDF' : 'Select PDF'}
                            </Button>
                            {pdfFile && (
                                <Typography variant="caption" sx={{ ml: 2 }} color="success.main">
                                    {pdfFile.name} ({formatFileSize(pdfFile.size)})
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="Container Name"
                                value={containerName}
                                onChange={(e) => setContainerName(e.target.value)}
                                size="small"
                                fullWidth
                            />
                            <TextField
                                label="File Name (path)"
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                size="small"
                                fullWidth
                                placeholder="e.g., path/to/document.pdf"
                            />
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Form Info (for extractor) */}
            {testType === 'extractor' && (
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Form Info</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormControlLabel
                            control={(
                                <Switch
                                    checked={useDefaultFormInfo}
                                    onChange={(e) => {
                                        setUseDefaultFormInfo(e.target.checked);
                                        setParserResultsError("");
                                    }}
                                />
                              )}
                            label="Use form info from prompt name"
                            sx={{ mb: 2 }}
                        />

                        {useDefaultFormInfo ? (
                            <Box>
                                {getDefaultFormInfo() ? (
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Chip
                                            label={`Type: ${getDefaultFormInfo()!.type}`}
                                            color="primary"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`Tax Year: ${getDefaultFormInfo()!.taxYear}`}
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Box>
                                ) : (
                                    <Alert severity="warning">
                                        Could not parse form info from prompt name. Expected format: extractor/&#123;formType&#125;-extract-only[-&#123;taxYear&#125;].txt
                                    </Alert>
                                )}
                            </Box>
                        ) : (
                            <TextField
                                value={parserResultsJson}
                                onChange={(e) => {
                                    setParserResultsJson(e.target.value);
                                    setParserResultsError("");
                                }}
                                multiline
                                rows={6}
                                fullWidth
                                placeholder='[{"form": {"type": "1040", "page": 1}, "taxYear": 2023, "isOnList": true, "isStateTaxReturn": false, "pageNumber": 1, "confidence": 0.95, "reasoning": "..."}]'
                                error={!!parserResultsError}
                                helperText={parserResultsError || "Enter JSON array of TaxReturnPage objects"}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                    },
                                }}
                            />
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Run Test Button */}
            <Button
                variant="contained"
                color="primary"
                startIcon={testLoading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                onClick={handleRunTest}
                disabled={!canRunTest() || testLoading}
                fullWidth
            >
                {testLoading ? 'Running Test...' : 'Run Test'}
            </Button>

            {/* Test Results */}
            {testResult && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Test Results
                        {testResult.success ? (
                            <Chip label="Success" color="success" size="small" sx={{ ml: 2 }} />
                        ) : (
                            <Chip label="Failed" color="error" size="small" sx={{ ml: 2 }} />
                        )}
                    </Typography>

                    {testResult.error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {testResult.error}
                        </Alert>
                    )}

                    {/* Stats */}
                    {(testResult.usage || testResult.cost || testResult.processingTime) && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                            {testResult.processingTime && (
                                <Chip
                                    label={`Time: ${(testResult.processingTime / 1000).toFixed(2)}s`}
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            {testResult.cost && (
                                <Chip
                                    label={`Cost: ${testResult.cost.formatted}`}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                />
                            )}
                            {testResult.usage && (
                                <Chip
                                    label={`Tokens: ${testResult.usage.inputTokens.toLocaleString()} in / ${testResult.usage.outputTokens.toLocaleString()} out`}
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            {testResult.chunksProcessed && (
                                <Chip
                                    label={`Chunks: ${testResult.chunksProcessed}`}
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                        </Box>
                    )}

                    {/* Results JSON */}
                    {testResult.results && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Results</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TextField
                                    value={JSON.stringify(testResult.results, null, 2)}
                                    multiline
                                    fullWidth
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                        },
                                    }}
                                />
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Paper>
            )}
        </Box>
    );
};

export default PromptTester;
